/* eslint-disable react/jsx-boolean-value */
import React from "react";
import PropTypes from "prop-types";
import Typography from "../typography";

/**
 * Breadcrumb component to display a breadcrumb trail.
 * Emits schema.org BreadcrumbList microdata when isShowSchema is true.
 * @param {Object[]} items - An array of objects representing breadcrumb items.
 * @param {string} items[].url - The URL path for the breadcrumb item.
 * @param {string} items[].label - The label/text for the breadcrumb item.
 * @param {string} catalogId - Catalog identifier used to prefix non-india URLs.
 * @param {boolean} [isShowSchema=true] - Whether to emit microdata attributes.
 * @param {boolean} [isFromMicrosite=false] - Applies microsite-specific padding.
 * @returns {JSX.Element} - Breadcrumb component JSX.
 */
const Breadcrumb = ({ items, catalogId, isShowSchema = true, isFromMicrosite = false }) => {
  const schemaList = isShowSchema ? { itemScope: true, itemType: "https://schema.org/BreadcrumbList" } : {};

  const schemaListItem = isShowSchema
    ? { itemProp: "itemListElement", itemScope: true, itemType: "https://schema.org/ListItem" }
    : {};

  return (
    <div {...schemaList} className="navigation" id="product-breadcrumb">
      <nav
        className={`py-12 ${isFromMicrosite ? "px-16 sm:px-40" : "sm:px-0"}`}
        aria-label="breadcrumb"
        id="breadcrumbs"
      >
        <ul className="flex w-full flex-wrap items-center truncate md:flex-nowrap">
          {items?.map(({ url, label }, index) => {
            const href = catalogId === "india" ? url : index === 0 ? `/${catalogId}/gifts-lp` : `/${catalogId}${url}`;
            const isLast = index === items.length - 1;

            if (isLast) {
              return (
                <li key={index} className="inline-flex items-center" {...schemaListItem}>
                  <Typography Tag={"p"} variant="h6" classes="flex items-center text-12 text-grey-500" itemProp="name">
                    {label}
                  </Typography>
                  {isShowSchema && <meta itemProp="position" content={String(index + 1)} />}
                </li>
              );
            }

            return (
              <li key={index} className="inline-flex items-center capitalize" {...schemaListItem}>
                <a
                  href={href}
                  itemProp="item"
                  className="flex items-center text-12 text-grey-400 hover:text-blue-600 focus:text-blue-600 focus:outline-none"
                >
                  <span itemProp="name">{label}</span>
                </a>
                <span
                  aria-hidden="true"
                  className="flex size-12 flex-shrink-0 items-center justify-center text-16 text-grey-400"
                >
                  /
                </span>
                {isShowSchema && <meta itemProp="position" content={String(index + 1)} />}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      label: PropTypes.string.isRequired,
      svg: PropTypes.bool,
    }),
  ).isRequired,
  catalogId: PropTypes.string.isRequired,
  isShowSchema: PropTypes.bool,
  isFromMicrosite: PropTypes.bool,
};

export default Breadcrumb;
